"use client";

import {
    Box,
    TextInput,
    NumberInput,
    Checkbox,
    Button,
    Stack,
    Group,
    Text,
    rem,
    useMantineColorScheme,
    LoadingOverlay,
    Title,
    Image,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { UseFormReturnType } from "@mantine/form";
import type { Form } from "@bi-studio/shared/lib";
import { BaseContainer } from "@/components";
import logo from "@bi-studio/shared/assets/logo.png";

interface FullscreenFormDisplayProps {
    form: Form;
    mantineForm: UseFormReturnType<any>;
    sortedFields: any[];
    isFormIncomplete: boolean;
    submitting: boolean;
    onSubmit: (values: any) => Promise<void>;
    handleValidationError: () => void;
}

export function FullscreenFormDisplay({
    form,
    mantineForm,
    sortedFields,
    isFormIncomplete,
    submitting,
    onSubmit,
    handleValidationError,
}: FullscreenFormDisplayProps) {
    const { colorScheme } = useMantineColorScheme();


    const config = (form.config as Record<string, any>) || {};

    const backgroundStyle = {
        backgroundImage: config.backgroundImage ? `url(${config.backgroundImage})` : undefined,
        backgroundColor: config.backgroundColor || (colorScheme === 'dark' ? 'var(--mantine-color-dark-9)' : 'var(--mantine-color-gray-0)'),
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
    };

    return (
        <Box
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
                ...backgroundStyle
            }}
        >

            {/* Scrollable Content Area */}
            <Box style={{
                flex: 1,
                overflow: 'auto',
                padding: `${rem(64)} ${rem(24)}`,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <Box maw={rem(640)} w="100%">
                    <BaseContainer
                        p={0}
                        style={{
                            position: 'relative',
                            backgroundColor: colorScheme === 'dark' ? 'rgba(29, 30, 32, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                            backdropFilter: 'blur(20px)',
                            borderRadius: 'var(--mantine-radius-xl)',
                            border: '1px solid var(--modal-border)',
                            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                            overflow: 'hidden'
                        }}
                    >
                        <LoadingOverlay visible={submitting} overlayProps={{ blur: 2 }} />

                        <Box p={rem(40)}>
                            <Stack gap="xl">
                                <Box>
                                    <Title order={2} size="h3" fw={800} style={{ letterSpacing: '-0.02em' }}>
                                        {form.name}
                                    </Title>
                                    {form.description && (
                                        <Text c="dimmed" size="sm" mt={8} style={{ lineHeight: 1.6 }}>
                                            {form.description}
                                        </Text>
                                    )}
                                </Box>

                                <form onSubmit={mantineForm.onSubmit(onSubmit, handleValidationError)}>
                                    <Stack gap="lg">
                                        {sortedFields.map((field) => (
                                            <div key={field.name}>
                                                {field.type === 'number' && (
                                                    <NumberInput
                                                        label={field.label}
                                                        placeholder={`Enter ${field.label.toLowerCase()}`}
                                                        required={field.required}
                                                        withAsterisk={field.required}
                                                        size="md"
                                                        radius="md"
                                                        {...mantineForm.getInputProps(field.name)}
                                                    />
                                                )}
                                                {field.type === 'date' && (
                                                    <DateInput
                                                        label={field.label}
                                                        placeholder="Pick date"
                                                        required={field.required}
                                                        withAsterisk={field.required}
                                                        size="md"
                                                        radius="md"
                                                        {...mantineForm.getInputProps(field.name)}
                                                    />
                                                )}
                                                {field.type === 'checkbox' && (
                                                    <Checkbox
                                                        label={field.label}
                                                        required={field.required}
                                                        size="md"
                                                        {...mantineForm.getInputProps(field.name, { type: 'checkbox' })}
                                                    />
                                                )}
                                                {(field.type === 'text' || !['number', 'date', 'checkbox'].includes(field.type)) && (
                                                    <TextInput
                                                        label={field.label}
                                                        placeholder={`Enter ${field.label.toLowerCase()}`}
                                                        required={field.required}
                                                        withAsterisk={field.required}
                                                        size="md"
                                                        radius="md"
                                                        {...mantineForm.getInputProps(field.name)}
                                                    />
                                                )}
                                            </div>
                                        ))}

                                        <Group justify="flex-end" mt="xl">
                                            <Button
                                                type="submit"
                                                loading={submitting}
                                                disabled={isFormIncomplete}
                                                size="lg"
                                                color="black"
                                                radius="md"
                                                fullWidth
                                            >
                                                Submit Information
                                            </Button>
                                        </Group>
                                    </Stack>
                                </form>
                            </Stack>
                        </Box>

                        {/* App Footer/Branding */}
                        <Box p="md" style={{
                            textAlign: 'center',
                            borderTop: '1px solid var(--modal-border)',
                            backgroundColor: colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.02)'
                        }}>
                            <Group justify="center" gap="xs">
                                <Text size="xs" c="dimmed" fw={600} style={{ letterSpacing: rem(1) }}>
                                    Powered by
                                </Text>
                                <Image
                                    src={typeof logo === 'string' ? logo : (logo as any).src}
                                    alt="BI Studio Logo"
                                    h={24}
                                    w="auto"
                                    style={{
                                        mixBlendMode: colorScheme === 'dark' ? 'screen' : 'multiply',
                                        filter: colorScheme === 'dark' ? 'invert(1) hue-rotate(180deg) brightness(1.2)' : 'none',
                                        opacity: 0.7
                                    }}
                                />
                            </Group>
                        </Box>
                    </BaseContainer>
                </Box>
            </Box>
        </Box>
    );
}
